import React, { useState, ReactNode, useRef, useEffect } from "react";
import { Alert, Snackbar, Button, CircularProgress } from "@mui/material";
import {
  DeepDiveToastContext,
  DeepDiveToastData,
} from "./DeepDiveToastContext";
import { useServices } from "../services/Services";
import { useNavigate } from "react-router-dom";
import { useDeepDiveTracking } from "./DeepDiveTrackingContext";

interface OpenAnalysisButtonProps {
  analysisId: string | null;
  analysisType: "event" | "topic";
  onClose: () => void;
}

const OpenAnalysisButton: React.FC<OpenAnalysisButtonProps> = ({
  analysisId,
  analysisType,
  onClose,
}) => {
  const navigate = useNavigate();

  if (!analysisId) {
    return (
      <Button variant="contained" size="small" disabled sx={{ ml: 1 }}>
        <CircularProgress size={16} />
      </Button>
    );
  }

  const handleClick = () => {
    const route =
      analysisType === "event"
        ? `/event-analysis/${analysisId}`
        : analysisType === "topic"
          ? `/topic-analysis/${analysisId}`
          : "/"; // fallback to home
    navigate(route);
    onClose();
  };

  return (
    <Button
      variant="contained"
      size="small"
      onClick={handleClick}
      sx={{ ml: 1 }}
    >
      Open Deep-Dive
    </Button>
  );
};

interface DeepDiveToastProviderProps {
  children: ReactNode;
}

export const DeepDiveToastProvider: React.FC<DeepDiveToastProviderProps> = ({
  children,
}) => {
  const services = useServices();
  const { data: user } = services.User.useGetCurrent();
  const { updateDeepDiveStatus, removeDeepDiveTracking } =
    useDeepDiveTracking();

  // Tracking state for deep dive requests
  const [trackedEventId, setTrackedEventId] = useState<string | null>(null);
  const [trackedTopic, setTrackedTopic] = useState<string | null>(null);
  const [eventContext, setEventContext] = useState<{
    eventName?: string;
    sourceDomain?: string;
  } | null>(null);
  const showEventDeepDiveToast = (
    eventId: string,
    eventName?: string,
    sourceDomain?: string,
  ) => {
    setTrackedEventId(eventId);
    setTrackedTopic(null);
    setEventContext({ eventName, sourceDomain });
  };

  const showTopicDeepDiveToast = (topic: string) => {
    setTrackedTopic(topic);
    setTrackedEventId(null);
  };

  const clearDeepDiveToast = () => {
    setTrackedEventId(null);
    setTrackedTopic(null);
    setEventContext(null);
  };
  const { data: currentEventRequest } =
    services.Request.useGetEventDeepDiveRequest(
      trackedEventId || "",
      user?.id || "",
    );
  const { data: currentTopicRequest } =
    services.Request.useGetTopicDeepDiveRequest(
      trackedTopic || "",
      user?.id || "",
    );

  const { data: analysis } = services.Analysis.useGetAnalysisByRequestId(
    (currentEventRequest?.status === "Done" ? currentEventRequest.id : "") ||
      (currentTopicRequest?.status === "Done" ? currentTopicRequest.id : ""),
  );
  const lastEventStatusRef = useRef<string | null>(null);
  const lastTopicStatusRef = useRef<string | null>(null);

  // Helper functions to derive toast data during render
  const getEventToastData = (): DeepDiveToastData | null => {
    if (!currentEventRequest || !trackedEventId) return null;

    const truncateEventName = (name: string, maxLength: number = 40) => {
      if (name.length <= maxLength) return name;
      const truncated = name.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(" ");
      return lastSpace > maxLength * 0.7
        ? truncated.substring(0, lastSpace) + "..."
        : truncated + "...";
    };

    // Use stored context
    const eventName = eventContext?.eventName;
    const sourceDomain = eventContext?.sourceDomain;
    const eventInfo = eventName ? `"${truncateEventName(eventName)}"` : "event";
    const sourceInfo = sourceDomain ? ` from ${sourceDomain}` : "";

    const message =
      currentEventRequest.status === "New"
        ? `${eventInfo}${sourceInfo}\nEvent deep-dive requested - will be processed in a few seconds`
        : currentEventRequest.status === "Pending"
          ? `${eventInfo}${sourceInfo}\nProcessing deep-dive analysis - will be done in less than a minute`
          : currentEventRequest.status === "Done"
            ? `${eventInfo}${sourceInfo}\nDeep-dive analysis completed`
            : currentEventRequest.status === "Failed"
              ? `${eventInfo}${sourceInfo}\nEvent deep-dive request failed`
              : `${eventInfo}${sourceInfo}\nDeep-dive analysis status: ${currentEventRequest.status}`;

    const severity =
      currentEventRequest.status === "Done"
        ? "success"
        : currentEventRequest.status === "Failed"
          ? "error"
          : "info";

    return {
      type: "event",
      message,
      severity,
      requestId: currentEventRequest.id,
      status: currentEventRequest.status || "",
      eventId: trackedEventId,
      ...(eventName && { eventName }),
      ...(sourceDomain && { sourceDomain }),
    };
  };

  const getTopicToastData = (): DeepDiveToastData | null => {
    if (!currentTopicRequest || !trackedTopic) return null;

    const truncateTopic = (topic: string, maxLength: number = 40) => {
      if (topic.length <= maxLength) return topic;
      const truncated = topic.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(" ");
      return lastSpace > maxLength * 0.7
        ? truncated.substring(0, lastSpace) + "..."
        : truncated + "...";
    };

    const topicInfo = trackedTopic
      ? `"${truncateTopic(trackedTopic)}"`
      : "topic";

    const message =
      currentTopicRequest.status === "New"
        ? `${topicInfo}\nTopic deep-dive requested - will be processed in a few seconds`
        : currentTopicRequest.status === "Pending"
          ? `${topicInfo}\nProcessing deep-dive analysis - will be done in less than a minute`
          : currentTopicRequest.status === "Done"
            ? `${topicInfo}\nDeep-dive analysis completed`
            : currentTopicRequest.status === "Failed"
              ? `${topicInfo}\nTopic deep-dive request failed`
              : `${topicInfo}\nDeep-dive analysis status: ${currentTopicRequest.status}`;

    const severity =
      currentTopicRequest.status === "Done"
        ? "success"
        : currentTopicRequest.status === "Failed"
          ? "error"
          : "info";

    return {
      type: "topic",
      message,
      severity,
      requestId: currentTopicRequest.id,
      status: currentTopicRequest.status || "",
      topic: trackedTopic,
    };
  };

  // Handle event deep dive request status updates
  useEffect(() => {
    if (!currentEventRequest) return;

    const currentStatus = currentEventRequest.status;

    if (currentStatus && currentStatus !== lastEventStatusRef.current) {
      const eventName = eventContext?.eventName;
      const sourceDomain = eventContext?.sourceDomain;

      // Track deep dive status globally
      if (currentStatus === "New" || currentStatus === "Pending") {
        updateDeepDiveStatus({
          type: "event",
          eventId: trackedEventId || "",
          eventName: eventName,
          sourceDomain: sourceDomain,
          status: "pending",
          updatedAt: new Date(),
        });
      } else if (currentStatus === "Done") {
        // Remove from tracking when done - completed items are shown via regular Analysis queries
        removeDeepDiveTracking("event", trackedEventId || "");
        // Invalidate the event deep dives query to refetch the list
        if (user?.id) {
          services.Analysis.invalidateEventDeepDives(user.id);
        }
        // Toast stays visible with the "Open Deep-Dive" action until the user
        // dismisses it via the Alert onClose or Open button (both wired to
        // clearDeepDiveToast). Auto-clearing here would race the render and
        // hide the Done message before the user could see the Open button.
      } else if (currentStatus === "Failed") {
        updateDeepDiveStatus({
          type: "event",
          eventId: trackedEventId || "",
          eventName: eventName,
          sourceDomain: sourceDomain,
          status: "failed",
          requestId: currentEventRequest.id,
          updatedAt: new Date(),
        });
      }

      lastEventStatusRef.current = currentStatus;
    }
  }, [
    currentEventRequest,
    trackedEventId,
    eventContext,
    updateDeepDiveStatus,
    removeDeepDiveTracking,
    user?.id,
    services.Analysis,
  ]);

  // Handle topic deep dive request status updates
  useEffect(() => {
    if (!currentTopicRequest) return;

    const currentStatus = currentTopicRequest.status;

    if (currentStatus && currentStatus !== lastTopicStatusRef.current) {
      // Track deep dive status globally
      if (currentStatus === "New" || currentStatus === "Pending") {
        updateDeepDiveStatus({
          type: "topic",
          topic: trackedTopic || "",
          status: "pending",
          updatedAt: new Date(),
        });
      } else if (currentStatus === "Done") {
        // Remove from tracking when done - completed items are shown via regular Analysis queries
        removeDeepDiveTracking("topic", trackedTopic || "");
        // Invalidate the topic deep dives query to refetch the list
        if (user?.id) {
          services.Analysis.invalidateTopicDeepDives(user.id);
        }
        // Toast stays visible with the "Open Deep-Dive" action until the user
        // dismisses it via the Alert onClose or Open button (both wired to
        // clearDeepDiveToast). Auto-clearing here would race the render and
        // hide the Done message before the user could see the Open button.
      } else if (currentStatus === "Failed") {
        updateDeepDiveStatus({
          type: "topic",
          topic: trackedTopic || "",
          status: "failed",
          requestId: currentTopicRequest.id,
          updatedAt: new Date(),
        });
      }

      lastTopicStatusRef.current = currentStatus;
    }
  }, [
    currentTopicRequest,
    trackedTopic,
    updateDeepDiveStatus,
    removeDeepDiveTracking,
    user?.id,
    services.Analysis,
  ]);

  const handleRetryRequest = async () => {
    if (!user?.id) return;

    try {
      if (trackedTopic) {
        // Retry topic deep dive
        await services.Request.createTopicDeepDiveRequest(
          trackedTopic,
          user.id,
        );
      } else if (trackedEventId) {
        // Retry event deep dive
        await services.Request.createEventDeepDiveRequest(
          trackedEventId,
          user.id,
        );
      }
      clearDeepDiveToast();
    } catch (error) {
      console.error("Failed to retry request:", error);
    }
  };

  const getToastIcon = (toastData: DeepDiveToastData | null) => {
    if (toastData?.status === "Pending") {
      return <CircularProgress size={16} sx={{ color: "inherit", mr: 1 }} />;
    }
    return null;
  };

  // Derive toast data during render
  const toastData = trackedEventId
    ? getEventToastData()
    : trackedTopic
      ? getTopicToastData()
      : null;
  const isOpen = toastData !== null;

  return (
    <DeepDiveToastContext.Provider
      value={{
        showEventDeepDiveToast,
        showTopicDeepDiveToast,
        currentDeepDiveToast: toastData,
        clearDeepDiveToast,
      }}
    >
      {children}

      {/* Deep Dive Toast Snackbar */}
      {isOpen && toastData && (
        <Snackbar
          open={isOpen}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          style={{ marginBottom: 16, marginLeft: 16 }}
        >
          <Alert
            severity={toastData.severity}
            onClose={clearDeepDiveToast}
            icon={getToastIcon(toastData)}
            action={
              toastData.status === "Failed" ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRetryRequest}
                  sx={{ ml: 1 }}
                >
                  Retry
                </Button>
              ) : toastData.status === "Done" ? (
                <OpenAnalysisButton
                  analysisId={analysis?.id || null}
                  analysisType={toastData.type}
                  onClose={clearDeepDiveToast}
                />
              ) : undefined
            }
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            <span style={{ whiteSpace: "pre-line" }}>{toastData.message}</span>
          </Alert>
        </Snackbar>
      )}
    </DeepDiveToastContext.Provider>
  );
};
